import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import {
  BanknotesIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  ExclamationTriangleIcon,
  UserCircleIcon,
  UsersIcon,
} from '@heroicons/react/24/solid';

import api from '../utils/api';

const isISODate = (s) => /^\d{4}-\d{2}-\d{2}$/.test(String(s || ''));

const toMoney = (n) => {
  const num = Number(n);
  if (Number.isNaN(num) || !Number.isFinite(num)) return '$0.00';
  return `$${num.toFixed(2)}`;
};

const toNumber2 = (n) => {
  const num = Number(n);
  if (Number.isNaN(num) || !Number.isFinite(num)) return 0;
  return Math.round(num * 100) / 100;
};

const getLedgerResults = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

const normalizeText = (text) => {
  if (typeof text !== 'string') return '';
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .trim()
    .replace(/\s+/g, ' ');
};

const normalizeStatus = (status) => String(status || '').trim().toLowerCase();

// Regla de negocio vigente: la pestaña de cobro total por matrizador
// solo considera pagos adicionales separados desde el 14 de abril de 2026.
// Los registros anteriores pertenecen al flujo histórico/global anterior.
const CASH_EXTRAS_TOTAL_START_DATE = '2026-04-14';

const isOnOrAfterCashExtrasTotalStartDate = (date) => {
  const value = String(date || '').trim();
  if (!isISODate(value)) return false;
  return value >= CASH_EXTRAS_TOTAL_START_DATE;
};

const collapseLedgerRowsByDayMatrizador = (rows) => {
  const map = new Map();

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const fecha = String(row?.fecha_documento || '').trim();
    const matrizador = normalizeText(String(row?.usuario_matrizador || ''));
    if (!fecha || !matrizador) return;

    const key = `${fecha}__${matrizador}`;
    const current = map.get(key);
    const currentStatus = normalizeStatus(current?.estado);
    const nextStatus = normalizeStatus(row?.estado);

    if (!current) {
      map.set(key, row);
      return;
    }

    if (currentStatus === 'cancelado' && nextStatus !== 'cancelado') return;
    if (currentStatus !== 'cancelado' && nextStatus === 'cancelado') {
      map.set(key, row);
      return;
    }

    const currentUpdated = String(current?.updated_at || current?.marcado_en || '');
    const nextUpdated = String(row?.updated_at || row?.marcado_en || '');
    if (nextUpdated > currentUpdated) {
      map.set(key, row);
      return;
    }

    if (nextUpdated === currentUpdated && Number(row?.id || 0) > Number(current?.id || 0)) {
      map.set(key, row);
    }
  });

  return Array.from(map.values());
};

const onlyEffectivePendingRows = (rows) => {
  return collapseLedgerRowsByDayMatrizador(rows)
    .filter((row) => normalizeStatus(row?.estado) === 'pendiente')
    .filter((row) => toNumber2(row?.total_extra || 0) > 0);
};

const fetchPendingLedgerRows = async () => {
  const pageSize = 500;
  let page = 1;
  let allRows = [];
  let keepReading = true;

  while (keepReading && page <= 50) {
    const res = await api.get('/api/audit/cash-extras/', {
      params: {
        mode: 'effective_pending_only',
        estado: 'pendiente',
        page,
        page_size: pageSize,
        _ts: Date.now(),
      },
    });

    const data = res.data;
    const batch = getLedgerResults(data);
    allRows = allRows.concat(batch);

    if (Array.isArray(data)) break;

    const hasNext = Boolean(data?.next);
    const count = Number(data?.count || 0);
    const totalPages = Number(data?.total_pages || (count > 0 ? Math.ceil(count / pageSize) : 1));

    keepReading = hasNext || page < totalPages;
    page += 1;
  }

  return allRows;
};

const breakdownEntries = (breakdown) => {
  if (!breakdown || typeof breakdown !== 'object') return [];
  return Object.entries(breakdown)
    .map(([name, data]) => ({
      name,
      count: Number(data?.count || 0),
      total: toNumber2(data?.total || 0),
    }))
    .filter((item) => item.total > 0 || item.count > 0);
};

const CashExtrasTotalByMatrizador = ({ refreshToken = 0, onLoadingChange }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [ledgerRows, setLedgerRows] = useState([]);
  const [selectedMatrizador, setSelectedMatrizador] = useState('');
  const [checkedIds, setCheckedIds] = useState(() => new Set());
  const [lastPaidSummary, setLastPaidSummary] = useState(null);
  const [rangeLabel, setRangeLabel] = useState('');
  const [loadingNote, setLoadingNote] = useState('');
  const [error, setError] = useState('');

  const setBusy = useCallback(
    (value) => {
      setLoading(value);
      if (typeof onLoadingChange === 'function') onLoadingChange(value);
    },
    [onLoadingChange]
  );

  const fetchPendingLedgers = useCallback(async () => {
    setBusy(true);
    setError('');
    setLoadingNote('Leyendo pendientes desde la base de datos...');

    try {
      const rawRows = await fetchPendingLedgerRows();
      const pendingRows = onlyEffectivePendingRows(rawRows).filter((row) =>
        isOnOrAfterCashExtrasTotalStartDate(row?.fecha_documento)
      );

      const dates = pendingRows
        .map((row) => String(row?.fecha_documento || ''))
        .filter(isISODate)
        .sort((a, b) => a.localeCompare(b));

      setRangeLabel(dates.length > 0 ? `${dates[0]} a ${dates[dates.length - 1]}` : '');

      const rows = pendingRows.sort((a, b) => {
        const byMatrizador = String(a?.usuario_matrizador || '').localeCompare(
          String(b?.usuario_matrizador || ''),
          'es'
        );
        if (byMatrizador !== 0) return byMatrizador;
        return String(b?.fecha_documento || '').localeCompare(String(a?.fecha_documento || ''));
      });

      setLedgerRows(rows);
    } catch (err) {
      console.error(err);
      setLedgerRows([]);
      setRangeLabel('');
      setError('No se pudo cargar el total pendiente por matrizador.');
      toast.error('No se pudo cargar el total pendiente por matrizador.');
    } finally {
      setLoadingNote('');
      setBusy(false);
    }
  }, [setBusy]);

  useEffect(() => {
    fetchPendingLedgers();
  }, [fetchPendingLedgers, refreshToken]);

  const groups = useMemo(() => {
    const map = new Map();

    ledgerRows.forEach((row) => {
      const label = String(row?.usuario_matrizador || 'SIN MATRIZADOR').trim() || 'SIN MATRIZADOR';
      const key = normalizeText(label) || 'SIN MATRIZADOR';
      if (!map.has(key)) {
        map.set(key, {
          key,
          usuario_matrizador: label,
          rows: [],
          total: 0,
        });
      }

      const group = map.get(key);
      group.rows.push(row);
      group.total = toNumber2(group.total + toNumber2(row?.total_extra || 0));
    });

    return Array.from(map.values()).sort((a, b) => {
      if (b.total !== a.total) return b.total - a.total;
      return String(a.usuario_matrizador).localeCompare(String(b.usuario_matrizador), 'es');
    });
  }, [ledgerRows]);

  const selectedGroup = useMemo(() => {
    return groups.find((g) => g.key === selectedMatrizador) || null;
  }, [groups, selectedMatrizador]);

  const selectedRowIdsKey = useMemo(() => {
    if (!selectedGroup) return '';
    return selectedGroup.rows.map((row) => String(row.id)).join('|');
  }, [selectedGroup]);

  useEffect(() => {
    if (groups.length === 0) {
      setSelectedMatrizador('');
      setCheckedIds(new Set());
      return;
    }

    if (selectedMatrizador && groups.some((g) => g.key === selectedMatrizador)) return;
    if (lastPaidSummary?.paidEverything) {
      setSelectedMatrizador('');
      setCheckedIds(new Set());
      return;
    }

    setSelectedMatrizador(groups[0].key);
  }, [groups, lastPaidSummary, selectedMatrizador]);

  useEffect(() => {
    if (!selectedGroup) {
      setCheckedIds(new Set());
      return;
    }

    setCheckedIds(new Set(selectedGroup.rows.map((row) => String(row.id))));
  }, [selectedGroup, selectedRowIdsKey]);

  const selectedRows = useMemo(() => selectedGroup?.rows || [], [selectedGroup]);
  const checkedRows = useMemo(() => {
    return selectedRows.filter((row) => checkedIds.has(String(row.id)));
  }, [checkedIds, selectedRows]);

  const selectedTotal = useMemo(() => {
    return checkedRows.reduce((acc, row) => toNumber2(acc + toNumber2(row?.total_extra || 0)), 0);
  }, [checkedRows]);

  const totalPending = useMemo(() => {
    return groups.reduce((acc, group) => toNumber2(acc + group.total), 0);
  }, [groups]);

  const toggleRow = (id) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      const key = String(id);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAll = () => {
    setCheckedIds(new Set(selectedRows.map((row) => String(row.id))));
  };

  const clearSelection = () => {
    setCheckedIds(new Set());
  };

  const handleMatrizadorChange = (value) => {
    setLastPaidSummary(null);
    setSelectedMatrizador(value);
  };

  const markSelectedPaid = async () => {
    if (!selectedGroup || checkedRows.length === 0) {
      toast.error('Selecciona al menos un día para registrar el pago.');
      return;
    }

    const ids = checkedRows.map((row) => row.id);
    const paidEverything = ids.length === selectedRows.length;

    setSubmitting(true);
    try {
      await api.patch('/api/audit/cash-extras/', {
        ids,
        estado: 'cancelado',
        notas: `Cobro total por matrizador: ${selectedGroup.usuario_matrizador}`,
      });

      setLastPaidSummary({
        matrizador: selectedGroup.usuario_matrizador,
        days: ids.length,
        total: selectedTotal,
        paidEverything,
      });

      toast.success(paidEverything ? 'PAGADO TODO.' : 'Pago parcial registrado.');
      await fetchPendingLedgers();
    } catch (err) {
      const msg = err?.response?.data?.error || 'No se pudo registrar el pago total.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const hasSelection = checkedRows.length > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="space-y-5"
    >
      <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-notaria-blue-1 shadow-2xl">
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-notaria-yellow via-emerald-300 to-cyan-300" />
        <div className="p-4 sm:p-6">
          <div className="flex flex-col xl:flex-row xl:items-end gap-4 justify-between">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 rounded-full border border-notaria-yellow/40 bg-notaria-yellow/10 px-3 py-1 text-xs font-bold text-notaria-yellow">
                <ClipboardDocumentCheckIcon className="h-4 w-4" />
                Cobro total por matrizador
              </div>
              <h2 className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight">
                Pendientes  consolidados
              </h2>
              <p className="mt-1 text-sm text-gray-300">
                Pendiente separado desde 2026-04-14 {rangeLabel ? `(${rangeLabel})` : ''}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 w-full xl:max-w-2xl">
              <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
                <div className="flex items-center gap-2 text-xs font-semibold text-gray-300">
                  <BanknotesIcon className="h-5 w-5 text-notaria-yellow" />
                  Total pendiente
                </div>
                <div className="mt-2 text-2xl font-extrabold">{toMoney(totalPending)}</div>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
                <div className="flex items-center gap-2 text-xs font-semibold text-gray-300">
                  <UsersIcon className="h-5 w-5 text-cyan-200" />
                  Matrizadores
                </div>
                <div className="mt-2 text-2xl font-extrabold">{groups.length}</div>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
                <div className="flex items-center gap-2 text-xs font-semibold text-gray-300">
                  <CalendarDaysIcon className="h-5 w-5 text-emerald-200" />
                  Días pendientes
                </div>
                <div className="mt-2 text-2xl font-extrabold">{ledgerRows.length}</div>
              </div>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-4">
            <div>
              <label className="block text-sm font-semibold mb-2">Matrizador</label>
              <select
                value={selectedMatrizador}
                onChange={(e) => handleMatrizadorChange(e.target.value)}
                disabled={loading || groups.length === 0}
                className="w-full p-3 rounded-xl bg-notaria-blue-2 text-white border border-white/10 focus:outline-none focus:ring-2 focus:ring-notaria-yellow disabled:opacity-60"
              >
                {groups.length === 0 ? (
                  <option value="">Sin pendientes</option>
                ) : (
                  groups.map((group) => (
                    <option key={group.key} value={group.key}>
                      {group.usuario_matrizador} - {group.rows.length} días - {toMoney(group.total)}
                    </option>
                  ))
                )}
              </select>
            </div>

            <div className="rounded-xl border border-white/10 bg-notaria-blue-2/40 p-4">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-semibold text-gray-200">Seleccionado</span>
                <span className="text-xl font-extrabold">{toMoney(selectedTotal)}</span>
              </div>
              <div className="mt-2 text-xs text-gray-300">
                {checkedRows.length} de {selectedRows.length} días marcados
              </div>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {lastPaidSummary ? (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            className={`rounded-2xl border p-4 sm:p-5 ${
              lastPaidSummary.paidEverything
                ? 'border-emerald-300/40 bg-emerald-500/10 text-emerald-50'
                : 'border-notaria-yellow/40 bg-notaria-yellow/10 text-yellow-50'
            }`}
          >
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
              <div className="flex items-start gap-3">
                <CheckCircleIcon className="h-8 w-8 shrink-0 text-emerald-300" />
                <div>
                  <div className="text-xl font-extrabold">
                    {lastPaidSummary.paidEverything ? 'PAGADO TODO' : 'Pago parcial registrado'}
                  </div>
                  <div className="mt-1 text-sm opacity-90">
                    {lastPaidSummary.matrizador} - {lastPaidSummary.days} día(s) - {toMoney(lastPaidSummary.total)}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {error ? (
        <div className="rounded-2xl border border-notaria-red/40 bg-notaria-red/10 p-4 text-sm text-red-100">
          <div className="flex items-center gap-2 font-bold">
            <ExclamationTriangleIcon className="h-5 w-5" />
            {error}
          </div>
        </div>
      ) : null}

      {loading ? (
        <div className="rounded-2xl bg-notaria-blue-1 bg-opacity-80 p-6 shadow-lg">
          <p className="text-gray-200">{loadingNote || 'Cargando saldos pendientes por matrizador...'}</p>
        </div>
      ) : groups.length === 0 ? (
        <div className="rounded-2xl border border-emerald-300/20 bg-notaria-blue-1 bg-opacity-80 p-8 text-center shadow-lg">
          <CheckCircleIcon className="mx-auto h-12 w-12 text-emerald-300" />
          <h3 className="mt-3 text-2xl font-extrabold">PAGADO TODO</h3>
          <p className="mt-2 text-sm text-gray-300">No hay valores extra pendientes desde el 14 de abril de 2026.</p>
        </div>
      ) : selectedGroup ? (
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_360px] gap-5">
          <section className="overflow-hidden rounded-2xl bg-notaria-blue-1 bg-opacity-80 shadow-lg">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between border-b border-white/10 bg-notaria-blue-2/40 p-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-lg font-extrabold">
                  <UserCircleIcon className="h-6 w-6 text-notaria-yellow" />
                  <span className="truncate">{selectedGroup.usuario_matrizador}</span>
                </div>
                <div className="mt-1 text-xs text-gray-300">
                  {selectedGroup.rows.length} días pendientes - {toMoney(selectedGroup.total)}
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={selectAll}
                  className="rounded-full border border-white/10 bg-white/10 px-3 py-2 text-xs font-bold hover:bg-notaria-yellow hover:text-notaria-blue-1"
                >
                  Marcar todos
                </button>
                <button
                  type="button"
                  onClick={clearSelection}
                  className="rounded-full border border-white/10 bg-white/10 px-3 py-2 text-xs font-bold hover:bg-notaria-red hover:text-white"
                >
                  Excluir todos
                </button>
              </div>
            </div>

            <div className="max-h-[620px] overflow-y-auto p-3 sm:p-4">
              <div className="space-y-3">
                {selectedRows.map((row) => {
                  const checked = checkedIds.has(String(row.id));
                  const entries = breakdownEntries(row.breakdown);

                  return (
                    <label
                      key={row.id}
                      className={`block rounded-2xl border p-4 transition-all ${
                        checked
                          ? 'border-notaria-yellow/60 bg-notaria-yellow/10 shadow-lg shadow-black/10'
                          : 'border-white/10 bg-white/[0.03] hover:bg-white/[0.06]'
                      }`}
                    >
                      <div className="flex flex-col lg:flex-row lg:items-start gap-3 justify-between">
                        <div className="flex items-start gap-3 min-w-0">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleRow(row.id)}
                            className="mt-1 h-5 w-5 shrink-0 accent-yellow-400"
                          />
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-base font-extrabold">{row.fecha_documento}</span>
                              <span className="rounded-full border border-white/10 bg-notaria-blue-2/50 px-2 py-1 text-[11px] font-semibold text-gray-200">
                                Pendiente
                              </span>
                            </div>
                            <div className="mt-2 flex flex-wrap gap-2">
                              {entries.length > 0 ? (
                                entries.slice(0, 4).map((item) => (
                                  <span
                                    key={item.name}
                                    className="rounded-full border border-white/10 bg-notaria-blue-2/50 px-2 py-1 text-[11px] text-gray-200"
                                  >
                                    {item.name}: {toMoney(item.total)}
                                  </span>
                                ))
                              ) : (
                                <span className="text-xs text-gray-300">Sin desglose</span>
                              )}
                              {entries.length > 4 ? (
                                <span className="rounded-full border border-white/10 bg-white/10 px-2 py-1 text-[11px] text-gray-200">
                                  +{entries.length - 4}
                                </span>
                              ) : null}
                            </div>
                          </div>
                        </div>

                        <div className="text-left lg:text-right">
                          <div className="text-xs font-semibold text-gray-300">Valor del día</div>
                          <div className="mt-1 text-xl font-extrabold">{toMoney(row.total_extra)}</div>
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          </section>

          <aside className="xl:sticky xl:top-6 self-start rounded-2xl border border-white/10 bg-notaria-blue-1 bg-opacity-90 p-5 shadow-2xl">
            <div className="flex items-center gap-2 text-sm font-bold text-gray-200">
              <ClipboardDocumentCheckIcon className="h-5 w-5 text-notaria-yellow" />
              Resumen de cobro
            </div>
            <div className="mt-4 rounded-xl border border-white/10 bg-notaria-blue-2/50 p-4">
              <div className="flex items-center justify-between gap-3 text-sm">
                <span className="text-gray-300">Días a cancelar</span>
                <span className="font-extrabold">{checkedRows.length}</span>
              </div>
              <div className="mt-3 flex items-center justify-between gap-3 text-sm">
                <span className="text-gray-300">Quedan excluidos</span>
                <span className="font-extrabold">{selectedRows.length - checkedRows.length}</span>
              </div>
              <div className="mt-4 border-t border-white/10 pt-4">
                <div className="text-xs font-semibold text-gray-300">Total a recibir</div>
                <div className="mt-1 text-4xl font-black tracking-tight">{toMoney(selectedTotal)}</div>
              </div>
            </div>

            <motion.button
              type="button"
              onClick={markSelectedPaid}
              disabled={!hasSelection || submitting}
              whileHover={{ scale: hasSelection && !submitting ? 1.02 : 1 }}
              whileTap={{ scale: hasSelection && !submitting ? 0.98 : 1 }}
              className={`mt-4 flex w-full items-center justify-center gap-2 rounded-full px-4 py-3 font-extrabold transition-all ${
                hasSelection && !submitting
                  ? 'bg-notaria-yellow text-notaria-blue-1 hover:bg-emerald-300'
                  : 'bg-notaria-blue-2 text-gray-300 cursor-not-allowed'
              }`}
            >
              <CheckCircleIcon className="h-5 w-5" />
              {submitting ? 'Registrando...' : 'Registrar pago'}
            </motion.button>

            <p className="mt-3 text-xs text-gray-300">
              Si excluyes días, esos valores continúan pendientes para un próximo cobro.
            </p>
          </aside>
        </div>
      ) : null}
    </motion.div>
  );
};

export default CashExtrasTotalByMatrizador;
